import { AssistantPart } from '@services/modules/AssistantStream'

/*
 * Helpers for confirmation cards (actions the assistant proposes). Pure, so the card logic is
 * testable without the store: which cards are still actionable, and marking a decision.
 */

export const CONFIRMATION_PART = 'confirmation'

/**
 * A card can still be confirmed/cancelled: proposed and not past its expiry. A card that is
 * 'executing' (the server has started the action and not recorded its outcome yet) is not.
 */
export const isActionable = (part: AssistantPart, now: Date = new Date()) =>
  part.type === CONFIRMATION_PART &&
  part.status === 'proposed' &&
  (!part.expiresAt || new Date(part.expiresAt) > now)

/**
 * Returns the parts with the card for `actionId` set to `status` (lower-case, as the server
 * stores it). Other parts are returned unchanged.
 */
export const withActionStatus = (
  parts: AssistantPart[] | undefined,
  actionId: string,
  status: string
): AssistantPart[] | undefined =>
  parts?.map(part =>
    part.type === CONFIRMATION_PART && part.actionId === actionId
      ? { ...part, status: status.toLowerCase() }
      : part
  )

/** The server has started the action and its outcome is not on record (yet). */
export const isExecuting = (status: string) =>
  status.toLowerCase() === 'executing'

/**
 * Whether a failed confirm/cancel call leaves it open what became of the action: no response at
 * all (network, timeout), a server error, or the plain text the response interceptor rejects
 * with for 408/409. A 4xx that carries a response is the server saying it refused the call.
 */
export const isOutcomeUnknown = (error: unknown): boolean => {
  if (typeof error === 'string') {
    return true
  }
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status
  return typeof status !== 'number' || status >= 500
}

/**
 * Whether a clarifying question loaded from the server has been answered. The server says so
 * (it is answered once a message of the user follows it); a server that does not say yet is
 * taken to have answered every question but the one the conversation ends with. An open
 * question keeps its options, so it can still be answered after a reload.
 */
const isClarificationAnswered = (m: any, isLast: boolean): boolean => {
  if (!m.clarification) {
    return false
  }
  return typeof m.clarificationAnswered === 'boolean'
    ? m.clarificationAnswered
    : !isLast
}

/**
 * Maps a server message DTO to the chat message shape the store renders.
 * `isLast`: the message is the last one of its conversation (see isClarificationAnswered).
 */
export const toChatMessage = (m: any, isLast = false) => ({
  id: m.id,
  role: m.role,
  content: m.content ?? '',
  citations: m.citations ?? undefined,
  clarification: m.clarification
    ? { ...m.clarification, messageId: m.id }
    : undefined,
  clarificationAnswered: isClarificationAnswered(m, isLast),
  // the option that was picked, shown as selected on its card
  clarificationSelectedValue:
    (isClarificationAnswered(m, isLast) && m.clarificationSelectedValue) ||
    undefined,
  feedbackRating: m.feedbackRating,
  parts: m.parts ?? undefined,
  // cut short (Interrupted) or ended in an error after part of it was shown (Failed)
  partial: m.state === 'Interrupted' || m.state === 'Failed' || undefined,
  excludedFromContext: m.excludedFromContext || undefined,
})
